import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// Only needed if you plan to manually call `window.DD.init`.
declare global {
  interface Window {
    DD: any;
  }
}

// Prevent duplicate React StrictMode mounts from breaking the widget in dev
// (No-op in production since StrictMode effects run once there.)
if (process.env.NODE_ENV !== 'production') {
  (window as any).__ddStrictGuard = true;
}

// Load Double the Donation script once and initialize the widget reliably
function loadDoubleTheDonation(): Promise<void> {
  // Poll until the global DD.init is available (handles late bootstrap)
  const waitForDD = (timeoutMs = 7000) =>
    new Promise<void>((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        if (window.DD && typeof window.DD.init === "function") return resolve();
        if (Date.now() - start > timeoutMs) return reject(new Error("Double the Donation not ready"));
        setTimeout(check, 60);
      };
      check();
    });

  // If already available, resolve immediately
  if (window.DD && typeof window.DD.init === "function") {
    return Promise.resolve();
  }

  // Ensure a single in-flight loader promise across SPA navigations
  const w = window as any;
  if (w.__ddLoaderPromise) {
    return w.__ddLoaderPromise as Promise<void>;
  }

  const scriptId = "dd-plugin-script";
  // Try to find an existing script by src first (in case it was added without an id)
  let script = document.querySelector('script[src*="doublethedonation.com/api/js/ddplugin.js"]') as HTMLScriptElement | null;
  if (!script) {
    script = document.getElementById(scriptId) as HTMLScriptElement | null;
  }

  w.__ddLoaderPromise = new Promise<void>((resolve, reject) => {
    const onReady = () => {
      waitForDD().then(resolve).catch(reject);
    };

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.crossOrigin = "anonymous";
      script.src = "https://doublethedonation.com/api/js/ddplugin.js";
      script.addEventListener(
        "load",
        () => {
          try {
            script!.setAttribute("data-loaded", "1");
          } catch {}
          onReady();
        },
        { once: true }
      );
      script.addEventListener(
        "error",
        (e) => {
          console.warn("Failed to load Double the Donation script", e);
          reject(e as any);
        },
        { once: true }
      );
      document.body.appendChild(script);
    } else {
      // Script tag exists — wait until DD is ready
      onReady();
    }
  });

  return w.__ddLoaderPromise as Promise<void>;
}

const FAQItem: React.FC<{ question: string; answer: string; }> = ({ question, answer }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="w-full border border-gray-300 rounded-md p-4 font-alice">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h4 className="text-lg font-semibold text-gray-800">{question}</h4>
        <svg
          className={`w-6 h-6 transform-gpu transition-transform duration-300 ${expanded ? "rotate-90" : "rotate-0"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </div>
      {expanded ? (
        <motion.div
          key="content"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-2 overflow-hidden"
        >
          <p className="text-gray-700">{answer}</p>
        </motion.div>
      ) : null}
    </div>
  );
};

const Matching: React.FC = () => {
  useEffect(() => {
    const selector = "#dd-container";

    const getEl = () => document.querySelector(selector) as HTMLElement | null;
    let el = getEl();
    if (!el) return;

    // If it already has content (iframe or any children), assume initialized.
    const hasWidget = (node: HTMLElement | null) => !!node && (node.querySelector("iframe") || node.childElementCount > 0);
    if (hasWidget(el)) return;

    // Dev-only: silence generic cross-origin "Script error." from the DTD script in React overlay
    const errorHandler = (evt: ErrorEvent) => {
      if (typeof evt?.filename === "string" && evt.filename.includes("doublethedonation.com")) {
        evt.preventDefault?.();
      }
    };
    window.addEventListener("error", errorHandler, true);

    let cancelled = false;
    let observer: MutationObserver | null = null;

    const ensureVisible = (node: HTMLElement) => {
      const style = window.getComputedStyle(node);
      return style.display !== "none" && style.visibility !== "hidden";
    };

    const tryInit = async () => {
      try {
        await loadDoubleTheDonation();
      } catch (e) {
        console.warn("DTD script not ready", e);
      }

      // Retry loop: wait for both DD.init and a visible container, then init.
      for (let i = 0; i < 20 && !cancelled; i++) {
        el = getEl();
        if (!el) break;
        if (window.DD && typeof window.DD.init === "function" && ensureVisible(el)) {
          try {
            // Observe for content to confirm success
            observer = new MutationObserver(() => {
              if (hasWidget(el!)) {
                el!.setAttribute("data-dd-initialized", "1");
                if (observer) observer.disconnect();
              }
            });
            observer.observe(el, { childList: true, subtree: true });

            window.DD.init({ parent: selector });

            // If it rendered immediately, set flag; otherwise observer will set it.
            if (hasWidget(el)) {
              el.setAttribute("data-dd-initialized", "1");
            }
            return; // done
          } catch (e) {
            // keep retrying
          }
        }
        await new Promise((r) => setTimeout(r, 150));
      }
    };

    // Kick off init
    tryInit();

    // Final safety: attempt on full page load & when tab becomes visible
    const lateInit = () => {
      if (cancelled) return;
      const node = getEl();
      if (!node || hasWidget(node)) return;
      tryInit();
    };
    window.addEventListener("load", lateInit, { once: true });
    document.addEventListener("visibilitychange", lateInit);

    return () => {
      cancelled = true;
      window.removeEventListener("error", errorHandler, true);
      window.removeEventListener("load", lateInit as any);
      document.removeEventListener("visibilitychange", lateInit as any);
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <section className="bg-gradient-to-b from-[#FAF0E6] to-white text-gray-800 font-alice">
      <section className="py-16 px-8 text-center bg-[#FAF0E6]">
        <h1 className="text-5xl font-alice text-gray-800 mb-4">Make Your Gift Go Further</h1>
        <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
          Many employers offer donation-matching programs that can multiply the impact of your gift. Learn how to take advantage of these opportunities to do even more good.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10 max-w-5xl mx-auto">
        </div>
        <section className="mt-16 max-w-4xl mx-auto text-left">
          <h2 className="text-3xl font-semibold text-gray-800 mb-4 font-alice">
            Matching Gifts
          </h2>
          <p className="text-lg text-gray-700 mb-4">
            Many companies match the donations their employees make to nonprofits. This is a great way to double or even triple the impact of your contribution!
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Use the search tool below to check if your employer offers a matching gift program:
          </p>

          {/* Double the Donation widget mounts here */}
          <div id="dd-container" className="mb-4 min-h-[64px]" />

          {/* Attribution (kept for compliance / trust) */}
          <div className="mb-8 text-sm">
            <a
              href="https://doublethedonation.com/matching-grant-resources/matching-gift-basics/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#9CAF88] hover:underline"
            >
              Matching Gift
            </a>{" "}
            and{" "}
            <a
              href="https://doublethedonation.com/matching-grant-resources/volunteer-grant-basics/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#9CAF88] hover:underline"
            >
              Volunteer Grant
            </a>{" "}
            information provided by
            <a href="https://doublethedonation.com" target="_blank" rel="noopener noreferrer" className="inline-block ml-2 align-middle">
              <img
                alt="Verified by Double the Donation"
                src="https://doublethedonation.com/api/img/powered-by.png"
                className="mt-1 shadow-md rounded inline-block"
              />
            </a>
          </div>
        </section>

        {/* Corporate Giving Programs and FAQs Section */}
        <section className="mt-16 max-w-4xl mx-auto text-left">
          <h2 className="text-3xl font-semibold text-gray-800 mb-4 font-alice">
            Corporate Giving Programs
          </h2>
          <p className="text-lg text-gray-700 mb-4">
            Many companies encourage workplace giving by matching donations, offering volunteer grants, or providing other incentives.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Check if your company participates to maximize your impact!
          </p>

          <h3 className="text-2xl font-semibold text-gray-800 mb-4 font-alice">
            Frequently Asked Questions
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            Here are some of the most common questions we receive about matching gifts and corporate giving.
          </p>
          <div className="bg-white bg-opacity-80 p-6 rounded-lg shadow-md">
            <div className="space-y-4">
              {(() => {
                const faqs = [
                  {
                    question: "What are employee matching gift programs?",
                    answer: "Your company may match your donation, doubling your impact.",
                  },
                  {
                    question: "What are volunteer grant programs?",
                    answer: "Some companies donate extra when you volunteer.",
                  },
                  {
                    question: "How do I request a matching gift or volunteer grant?",
                    answer: "A quick 5-minute form can start the process.",
                  },
                  {
                    question: "How is this information obtained?",
                    answer: "Data is provided by our partner, Double the Donation.",
                  },
                  {
                    question: "What if I still have questions?",
                    answer: "Contact your HR or our team for more details.",
                  },
                ];
                return (
                  <div className="space-y-4">
                    {faqs.map((faq, index) => (
                      <FAQItem key={index} question={faq.question} answer={faq.answer} />
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </section>
      </section>
      <section className="mt-20 bg-[#9CAF88] text-white py-12 px-6 rounded-xl shadow-lg max-w-5xl mx-auto text-center">
        <h2 className="text-3xl font-alice mb-4">Ready to Make an Impact?</h2>
        <p className="text-lg mb-6 max-w-2xl mx-auto">
          Your generosity supports real change. Join us in building sustainable futures for underserved communities.
        </p>
        <a href="/donate" className="inline-block bg-white text-[#9CAF88] font-semibold px-6 py-3 rounded-lg shadow hover:bg-gray-100 transition">
          Donate Now
        </a>
      </section>
    </section>
  );
};

export default Matching;